/**
 * ProfileDropdown — меню профиля в Topbar.
 *
 * Платформенно-адаптивное: на desktop — Radix DropdownMenu (компактные пункты
 * у триггера-аватара), на mobile/touch — нативная нижняя шторка BottomSheet с
 * крупными строками (≥48px), иконками 18px и safe-area снизу. Оба режима — через
 * общий `ActionMenu` (mobile-вариант закрывает прежний «TODO Wave 2: bottom sheet»).
 *
 * См. docs/ui-spec/system.profile-menu.{desktop,mobile}.md.
 */

import { Badge } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import {
	IconCoins,
	IconCompass,
	IconCrown,
	IconFileText,
	IconLifebuoy,
	IconLogout,
	IconSettings,
	IconSparkles,
	IconUserCircle,
} from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionMenu, type ActionMenuItem } from "@/components/shared/action-menu";
import { UserAvatar } from "@/components/shared/user-avatar";
import { type HeaderPoints, useIecProfile } from "@/hooks/use-iec-profile";
import { cn } from "@/lib/cn";
import { canOpenIecIdSection, openIecIdSection } from "@/lib/iec-id-portal";
import { performSignOut } from "@/lib/logout";
import { useCommerceVisible } from "@/lib/module-availability/commerce";
import { resolveUserAvatarUrl } from "@/lib/user-avatar-url";
import { useHowItWorksStore } from "@/stores/how-it-works";
import { type SubscriptionTier, useSessionStore } from "@/stores/session";

/**
 * Подпись бейджа уровня ЭКОСИСТЕМЫ. Марка, а не переводимая строка: «IEC+» в
 * любой локали пишется одинаково, как и «StudyHub».
 *
 * `free` бейджа не даёт: «IEC+ Free» — это не уровень, а его отсутствие.
 */
function iecPlusLabel(tier: string | null): string | null {
	if (tier === "pro") return "IEC+ Pro";
	if (tier === "plus") return "IEC+";
	return null;
}

function tierLabel(t: ReturnType<typeof useTranslation>["t"], tier: SubscriptionTier): string {
	if (tier === "pro") return t("profileMenu.tierPro");
	if (tier === "plus") return t("profileMenu.tierPlus");
	return t("profileMenu.tierFree");
}

export interface ProfileDropdownProps {
	/** Триггер по умолчанию — avatar size-8; можно переопределить (например, на mobile использовать IconUserCircle). */
	triggerClassName?: string;
	/** Размер avatar — desktop=8 (size-8), mobile=11 (size-11). */
	size?: 8 | 9 | 11;
	/**
	 * Минимальное меню — только профиль-лейбл + «Выйти». Используется в онбординге:
	 * пользователь не должен попадать в разделы приложения (профиль/настройки/
	 * биллинг/помощь/юр.документы) до завершения онбординга, но обязан иметь
	 * возможность выйти из аккаунта.
	 */
	minimal?: boolean;
}

export function ProfileDropdown({
	triggerClassName,
	size = 8,
	minimal = false,
}: ProfileDropdownProps) {
	const { t, i18n } = useTranslation(["settings", "common"]);
	const navigate = useNavigate();
	const user = useSessionStore((s) => s.user);
	const tier = useSessionStore((s) => s.tier);
	const banned = useSessionStore((s) => s.banned);
	const commerceVisible = useCommerceVisible();
	const openHowItWorks = useHowItWorksStore((s) => s.open);

	// Меню управляемое РАДИ запроса сводки: она нужна ровно тогда, когда на неё
	// смотрят. Фоновый опрос из шапки, висящей на каждом экране, — это лишний
	// запрос за каждого сидящего в приложении и ничего сверх того.
	const [open, setOpen] = useState(false);
	const iec = useIecProfile({ enabled: open && !minimal });

	const numberFmt = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

	const sizeClass = size === 11 ? "size-11" : size === 9 ? "size-9" : "size-8";
	const avatarSize = size === 11 ? "size-9" : "size-8";

	function onLogout() {
		// Полный выход: revoke сессии/cookie + bearer + очистка session-store,
		// query-кэша (in-memory + персист localStorage) + PowerSync. Без очистки
		// персиста auth.me reload «возвращал» в аккаунт, а разделы падали 401.
		void performSignOut().finally(() => {
			toast.success(t("logoutSuccess"));
			void navigate({ to: "/" });
		});
	}

	// Навигация через onSelect (а не href) — ActionMenu остаётся независимым от
	// типизированных Router-роутов и одинаков на desktop-dropdown и mobile-sheet.
	const items: ActionMenuItem[] = minimal
		? [
				{
					key: "logout",
					label: t("profileMenu.logout"),
					icon: <IconLogout size={18} stroke={1.75} />,
					tone: "destructive",
					separatorBefore: true,
					onSelect: onLogout,
				},
			]
		: [
				// Блок Баллов — УСЛОВНЫЙ. Его нет, пока человек не выдал сервису
				// data-scope `points_balance` в разделе «Сервисы» витрины (§0.3), и
				// пустое место здесь честнее нуля: «0 Баллов» сказало бы, что баланс
				// сгорел. Правило целиком — в `useIecProfile`.
				//
				// В приложении блока нет совсем: Баллы покупаются в экосистеме, и
				// баланс в меню App Store читает как «контент, купленный вне
				// приложения» (3.1.1). Правило — в `canOpenIecIdSection`.
				...(iec.points && canOpenIecIdSection("points")
					? [
							{
								key: "points",
								label: (
									<PointsLabel
										label={t("common:account.points")}
										approximateHint={t("common:account.pointsApproximate")}
										points={iec.points}
										formatted={numberFmt.format(iec.points.balance)}
									/>
								),
								icon: <IconCoins size={18} stroke={1.75} />,
								separatorBefore: true,
								// Через handoff, а не прямой ссылкой: по прямой человек
								// приземлится в витрине гостем и будет входить второй раз.
								onSelect: () => openIecIdSection("points"),
							} satisfies ActionMenuItem,
						]
					: []),
				// «Профиль» убран — вёл в тот же раздел, что «Настройки» (/settings →
				// /settings/profile). Оставляем один пункт «Настройки».
				{
					key: "manage-account",
					label: t("profileMenu.manageAccount"),
					icon: <IconUserCircle size={18} stroke={1.75} />,
					separatorBefore: true,
					// Единый профиль IEC ID: имя, фото, почта и согласия правятся ТОЛЬКО
					// там — здесь на них смотрят. Внешний переход, не роут приложения.
					onSelect: () => openIecIdSection("personal"),
				},
				{
					key: "settings",
					label: t("profileMenu.settings"),
					icon: <IconSettings size={18} stroke={1.75} />,
					onSelect: () => void navigate({ to: "/settings" }),
				},
				// Уровень доступа и оплата — одна страница. Пока коммерция скрыта,
				// пункта в меню нет: он вёл бы на закрытый роут.
				...(commerceVisible
					? [
							{
								key: "billing",
								label: t("profileMenu.billing"),
								icon: <IconCrown size={18} stroke={1.75} />,
								disabled: banned,
								onSelect: () => void navigate({ to: "/settings/billing" }),
							} satisfies ActionMenuItem,
						]
					: []),
				{
					key: "help",
					label: t("profileMenu.help"),
					icon: <IconLifebuoy size={18} stroke={1.75} />,
					separatorBefore: true,
					// Внутренний роут /faq (публичная страница помощи) — SPA-навигация.
					onSelect: () => void navigate({ to: "/faq" }),
				},
				{
					key: "replay-onboarding",
					label: t("profileMenu.replayOnboarding"),
					icon: <IconCompass size={18} stroke={1.75} />,
					// Открывает справку «Как это работает» — ту же модалку, что кнопка
					// дашборда (PRODUCT-NARRATIVE §7.2): два входа в ОДНО объяснение.
					// Прежний переход на /onboarding/tour?replay=true снят вместе с
					// туром из пяти открыток — уводить из приложения ради справки
					// неправильно, а сам роут удаляется.
					onSelect: () => openHowItWorks({ source: "profile-menu" }),
				},
				{
					key: "legal",
					label: t("profileMenu.legal"),
					icon: <IconFileText size={18} stroke={1.75} />,
					onSelect: () =>
						void navigate({
							to: "/legal/$kind",
							params: { kind: "documents" },
						}),
				},
				{
					key: "logout",
					label: t("profileMenu.logout"),
					icon: <IconLogout size={18} stroke={1.75} />,
					tone: "destructive",
					separatorBefore: true,
					onSelect: onLogout,
				},
			];

	return (
		<ActionMenu
			align="end"
			open={open}
			onOpenChange={setOpen}
			header={
				// Имя и аватар приходят из единого профиля; наше зеркало (session-store)
				// — запасной путь, и он же то, что видно, пока сводка едет или когда
				// экосистема недоступна (§10 п.1: рисуем из кэша, имя и аватар от
				// возраста не портятся).
				//
				// Почта — НАША, полная, а не `maskedEmail` из сводки. Маска в их
				// ответе существует для поверхностей, где адрес показывают ТРЕТЬЕМУ
				// лицу; собственное меню профиля — не такая поверхность: человек
				// смотрит свою же почту, которую только что вводил при входе, и
				// `la•••••••••@gmail.com` здесь не защищает ничего, зато мешает
				// понять, под каким из своих аккаунтов он сидит.
				//
				// Маска осталась запасным путём: если своего адреса вдруг нет,
				// показать частичный лучше, чем пустое место.
				<ProfileHeader
					userId={user?.id}
					name={iec.displayName ?? user?.name}
					email={user?.email ?? iec.maskedEmail ?? undefined}
					avatarUrl={resolveUserAvatarUrl({
						summary: iec.avatarUrl,
						local: user?.avatarUrl,
						hasLocalAvatar: user?.hasLocalAvatar,
					})}
					avatarSeed={user?.avatarSeed}
					tier={tier}
					iecPlus={iecPlusLabel(iec.planTier)}
					showTier={commerceVisible}
				/>
			}
			items={items}
			contentClassName="w-64"
			trigger={
				// БЕЗ обёртки <Tooltip>: ActionMenu рендерит триггер через
				// `DropdownMenuTrigger asChild` / `BottomSheetTrigger asChild`.
				// Radix Slot клонирует НЕПОСРЕДСТВЕННЫЙ child и навешивает на него
				// onClick/ref/onPointerDown. `Tooltip.Root` — провайдер контекста
				// (не DOM-узел), эти пропсы на кнопку НЕ форвардит → меню переставало
				// открываться (регресс bb13de1b «тултипы на иконочных кнопках»).
				// Подсказку даём нативным `title` + `aria-label`.
				<button
					type="button"
					aria-label={t("profileMenu.profile")}
					title={t("profileMenu.profile")}
					className={cn(
						"inline-flex items-center justify-center rounded-full cursor-pointer",
						"ring-2 ring-transparent hover:ring-border transition-shadow",
						sizeClass,
						triggerClassName,
					)}
				>
					<UserAvatar
						className={avatarSize}
						userId={user?.id}
						name={user?.name}
						avatarUrl={resolveUserAvatarUrl({ local: user?.avatarUrl })}
						avatarSeed={user?.avatarSeed}
					/>
				</button>
			}
		/>
	);
}

function ProfileHeader({
	userId,
	name,
	email,
	avatarUrl,
	avatarSeed,
	tier,
	iecPlus,
	showTier,
}: {
	userId: string | undefined;
	name: string | undefined;
	email: string | undefined;
	/** `null` — адреса ещё нет (см. `lib/user-avatar-url.ts`), рисуем переходную заглушку. */
	avatarUrl: string | null | undefined;
	avatarSeed: string | undefined;
	tier: SubscriptionTier;
	/** Подпись бейджа IEC+ либо `null`. См. `iecPlusLabel`. */
	iecPlus?: string | null;
	/**
	 * Показывать ли уровень тарифа.
	 *
	 * Тот же признак, что прячет продажу, — и это не совпадение, а требование
	 * App Store 3.1.1: подписка, купленная вне приложения, не должна быть в нём
	 * ВИДНА, пока её нельзя купить через IAP. Ревьюер увидел у демо-учётки
	 * бейдж «Pro» и завернул сборку 10.09.2026 именно на этом. Прежнее решение
	 * «продажу прячем, уровень показываем» отменено.
	 */
	showTier: boolean;
}) {
	const { t } = useTranslation(["settings", "common"]);
	return (
		<div className="flex items-start gap-2.5 py-1">
			<UserAvatar
				className="size-9"
				userId={userId}
				name={name}
				avatarUrl={avatarUrl}
				avatarSeed={avatarSeed}
			/>
			<div className="flex-1 min-w-0">
				<div className="text-sm font-medium text-foreground truncate">{name ?? "—"}</div>
				<div className="text-xs text-muted-foreground truncate">{email ?? ""}</div>
				{showTier ? (
					<div className="mt-1 flex items-center gap-1.5">
						<Badge
							variant="outline"
							className={cn(
								"text-2xs uppercase tracking-wider tabular-nums",
								tier === "pro" && "border-warning/30 text-warning",
								tier === "plus" && "border-primary/30 text-primary",
							)}
						>
							{tierLabel(t, tier)}
						</Badge>
						{/*
						Бейдж уровня ЭКОСИСТЕМЫ — рядом с нашим тарифом, а не вместо него:
						это два разных факта. Доступ в StudyHub решает тариф слева
						(`serviceTier` из сессии), IEC+ справа — что человек имеет в
						экосистеме. Принимать по нему решения о доступе нельзя (§9.1).
					*/}
						{iecPlus && (
							<Badge
								variant="outline"
								className="text-2xs border-primary/30 text-primary"
								title={t("common:account.iecPlusHint")}
							>
								{iecPlus}
							</Badge>
						)}
						{tier === "free" && !iecPlus && (
							<span className="inline-flex items-center gap-0.5 text-2xs text-muted-foreground">
								<IconSparkles size={11} stroke={1.75} />
								{t("profileMenu.upsell")}
							</span>
						)}
					</div>
				) : null}
			</div>
		</div>
	);
}

/**
 * Строка Баллов в меню: подпись слева, число справа.
 *
 * `approximate` — значение из памяти, источник сейчас недоступен (§10 п.3).
 * Помечаем знаком «≈» и подсказкой, а не рисуем как свежее: показать вчерашнюю
 * цифру сегодняшней — то же враньё, что нарисовать ноль вместо неизвестности.
 */
function PointsLabel({
	label,
	approximateHint,
	points,
	formatted,
}: {
	label: string;
	approximateHint: string;
	points: HeaderPoints;
	formatted: string;
}) {
	return (
		<span className="flex w-full items-center justify-between gap-3">
			<span className="truncate">{label}</span>
			<span
				className={cn(
					"shrink-0 text-xs tabular-nums",
					points.approximate ? "text-muted-foreground" : "text-foreground",
				)}
				{...(points.approximate ? { title: approximateHint } : {})}
			>
				{points.approximate ? `≈ ${formatted}` : formatted}
			</span>
		</span>
	);
}
